import "./upload.scss";

import { autoinject, observable, BindingEngine, Disposable } from "aurelia-framework";
import { DialogController } from "aurelia-dialog";
import moment from "moment";

import nameof from "../../../../common/nameof";
import { IGetPatientAdmissionPeriods, IPatientDialogOptions } from "../../../../interfaces/i-get-patient";
import { IUploadPatientDocumentQuery } from "./../../../../interfaces/i-get-patient";
import { PatientsService } from "./../../../../services/patient-service";
import { PatientDocuments } from "../patient-documents/patient-documents";
import { ITypeaheadOptions } from "../../../../interfaces/i-typeahead";
import { IEnumResponse } from "../../../../interfaces/i-enum";
import { getEnumFilterOptions } from "../../../../common/utilities/filter-manager";

@autoinject
export class Upload {
    private _patientsService: PatientsService;
    private _eaSubscriptions: Disposable[] = [];
    private _bindingEngine: BindingEngine;
    public controller: DialogController;
    public patientDocuments: PatientDocuments;
    public options: IPatientDialogOptions = {
        title: "",
        patientId: null
    };
    @observable({
        changeHandler: nameof<Upload>("selectedPatientVersionIdChanged")
    })
    public selectedPatientVersionId?: string = null;
    public admissionPeriods: IGetPatientAdmissionPeriods[] = [];
    public uploadTypeFilterOptions: IEnumResponse[] = [];
    public uploadTypes: ITypeaheadOptions[] = [];
    public selectedUploadTypes: ITypeaheadOptions[] = [];
    public uploadTypesFilterName: string = "";

    public constructor(controller: DialogController, patientsService: PatientsService, bindingEngine: BindingEngine) {
        this.controller = controller;
        this._patientsService = patientsService;
        this._bindingEngine = bindingEngine;
    }

    public async activate(options: IPatientDialogOptions) {
        Object.assign(this.options, options);
        this.admissionPeriods = await this._patientsService.getPatientAdmissionPeriodsNoPermission(
            this.options.patientId
        );
    }

    public attached() {
        this._eaSubscriptions.push(
            this._bindingEngine.collectionObserver(this.selectedUploadTypes).subscribe((splices: any) => {
                this.selectedUploadTypesChanged();
            })
        );
    }

    public selectedUploadTypesChanged() {
        this.patientDocuments?.loadFilesList();
    }

    public selectedPatientVersionIdChanged() {
        this.patientDocuments?.loadFilesList();
    }

    public uploadTypesFetch(filter: string): ITypeaheadOptions[] {
        if (this.patientDocuments?.searchedTypes?.length > 0) {
            this.uploadTypeFilterOptions = this.patientDocuments.searchedTypes;
        }
        return getEnumFilterOptions(filter, this.uploadTypeFilterOptions);
    }

    public getAdmissionPeriodLabel(period: IGetPatientAdmissionPeriods) {
        let startLabel = moment(period.admissionDate).format("MM/DD/YYYY");
        let end = period.isActive ? null : moment(period.dischargeDate).format("MM/DD/YYYY");
        let endLabel = end ? "- " + end : "";
        return `${startLabel} ${endLabel}${period.isActive ? "(Current Admission Period)" : ""}`;
    }

    public uploadDocument(patientId: string, newDocument: IUploadPatientDocumentQuery) {
        return this._patientsService.uploadDocument(patientId, newDocument);
    }

    public deleteDocument(patientId: string, documentId: string) {
        return this._patientsService.deleteDocument(patientId, documentId);
    }

    public getPatientDocuments(patientId: string) {
        return this._patientsService.getPatientDocuments(patientId, {
            patientVersionId: this.selectedPatientVersionId
        });
    }

    public getPatientFilteredDocuments(patientId: string) {
        let selectedUploadTypeIds: string[] = [];
        if (this.selectedUploadTypes.length > 0) {
            selectedUploadTypeIds = this.selectedUploadTypes.map((docType) => docType.value);
        }
        return this._patientsService.getPatientFilteredDocuments(patientId, {
            patientVersionId: this.selectedPatientVersionId,
            uploadTypeIds: selectedUploadTypeIds
        });
    }

    public detached() {
        if (this._eaSubscriptions.length > 0) {
            this._eaSubscriptions.forEach((sub) => sub.dispose());
        }
    }
}
